import { useEffect } from "react";
import { setQcSessionSection } from "@/lib/manufacturing/builderAssistantChecklistClient";
import type { BuilderAssistantObservation } from "@/lib/manufacturing/builderAssistantTypes";
import { BuilderAssistantChecklistItemRow } from "./BuilderAssistantChecklistItemRow";
import { BuilderAssistantChecklistSection } from "./BuilderAssistantChecklistSection";
import { BuilderAssistantLooseFindings } from "./BuilderAssistantLooseFindings";
import { useQcChecklistWalkModel } from "./useQcChecklistWalkModel";

export function BuilderAssistantChecklistWalk({
  sessionId,
  observations,
  disabled,
  focusCategoryId,
  photographedObservationIds = [],
  evidenceStamp = "",
  onChanged,
  onFlushPending,
  onOpenSession,
}: {
  sessionId: string;
  observations: readonly BuilderAssistantObservation[];
  disabled: boolean;
  focusCategoryId?: string | null;
  photographedObservationIds?: readonly string[];
  evidenceStamp?: string;
  onChanged: () => void | Promise<void>;
  onFlushPending?: (flush: () => Promise<{ ok: true } | { ok: false; error: string }>) => void;
  onOpenSession: (sessionId: string) => void;
}) {
  const model = useQcChecklistWalkModel({
    sessionId,
    observations,
    photographedObservationIds,
    evidenceStamp,
    onChanged,
    onFlushPending,
    onOpenSession,
  });

  useEffect(() => {
    if (!focusCategoryId) return;
    model.setOpenSectionId(focusCategoryId);
    document.getElementById(`qc-section-${focusCategoryId}`)?.scrollIntoView({ block: "start" });
  }, [focusCategoryId, model.focusStamp, model.setOpenSectionId]);

  if (model.empty) return null;
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">QC checklist</h3>
      {model.error ? <p className="text-sm text-red-700 dark:text-red-300">{model.error}</p> : null}
      {model.visibleCategories.map((category) => {
        const included = category.required || model.chosen.has(category.id);
        const open = model.openSectionId === category.id;
        const rows = included ? model.visibleItems.filter((item) => item.category_id === category.id) : [];
        const section = model.sectionRows.find((row) => row.category_id === category.id);
        const locked = disabled || Boolean(section?.submitted_at);
        return (
          <BuilderAssistantChecklistSection
            key={category.id}
            name={category.name}
            required={category.required}
            included={included}
            open={open}
            section={section}
            openFailures={model.openFailuresFor(category.id)}
            disabled={disabled}
            canSubmit={rows.length > 0}
            onToggleIncluded={(selectedNext) => {
              void setQcSessionSection(sessionId, category.id, selectedNext).then((res) => {
                if (!res.ok) {
                  model.setError(res.error);
                  return;
                }
                if (selectedNext) model.setOpenSectionId(category.id);
                else if (open) model.setOpenSectionId(null);
                void model.reload();
              });
            }}
            onToggleOpen={() => {
              void model.draft.persistDraft();
              const next = open ? null : category.id;
              model.setOpenSectionId(next);
              if (next && !included) {
                void setQcSessionSection(sessionId, category.id, true).then((res) => {
                  if (!res.ok) {
                    model.setError(res.error);
                    return;
                  }
                  void model.reload();
                });
              }
            }}
            onSubmit={() => model.submitCategory(category.id)}
            onCorrect={() => model.correctCategory(category.id)}
          >
            <div id={`qc-section-${category.id}`}>
              {rows.length > 0 ? (
                <ul className="space-y-2">
                  {rows.map((item) => {
                    const server = model.checks.find((row) => row.item_id === item.id);
                    const local = model.draft.pending[item.id];
                    const shown = local
                      ? {
                          item_id: item.id,
                          result: local.result,
                          note: local.note,
                          observation_id: server?.observation_id ?? null,
                          pass_url: server?.pass_url ?? null,
                          has_pass: server?.has_pass,
                        }
                      : server;
                    return (
                      <BuilderAssistantChecklistItemRow
                        key={item.id}
                        sessionId={sessionId}
                        item={item}
                        check={shown}
                        needsPhoto={model.failurePhotosRequired || item.failure_photo_required}
                        notesRequired={model.failureNotesRequired || item.failure_note_required}
                        note={model.draft.notes[item.id] ?? ""}
                        savedFailureCount={model.savedFailureCount(item.id, shown?.observation_id ?? null)}
                        localPhotos={model.photos.queue.filter((photo) => photo.itemId === item.id)}
                        disabled={locked}
                        onNote={(value) => {
                          model.setError(null);
                          model.draft.onNote(item.id, value);
                        }}
                        onPass={() => {
                          model.setError(null);
                          model.draft.mark(item.id, "pass");
                        }}
                        onFail={() => {
                          model.setError(null);
                          model.draft.mark(item.id, "fail");
                        }}
                        onNa={() => {
                          model.setError(null);
                          model.draft.mark(item.id, "na");
                        }}
                        onFailPhoto={(file) => {
                          model.setError(null);
                          model.draft.mark(item.id, "fail");
                          const accepted = model.photos.add(item.id, "fail", file, model.savedFailureCount(item.id, shown?.observation_id ?? null));
                          if (!accepted) model.setError("This item already has 3 failure photos.");
                        }}
                        onPassPhoto={(file) => {
                          model.setError(null);
                          model.draft.mark(item.id, "pass");
                          const accepted = model.photos.add(item.id, "pass", file, 0);
                          if (!accepted) model.setError("That pass photo is still sending.");
                        }}
                        onRemoveLocalPhoto={(id) => model.photos.remove(id)}
                        onRetryLocalPhoto={(id) => model.photos.retry(id)}
                      />
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-600 dark:text-zinc-400">No checks in this section.</p>
              )}
            </div>
          </BuilderAssistantChecklistSection>
        );
      })}
      <BuilderAssistantLooseFindings
        examples={model.examples}
        loose={model.loose}
        categories={model.visibleCategories}
        categoryByObservation={model.categoryByObservation}
        disabled={disabled}
        onCategory={(observationId, categoryId) => model.setCategoryByObservation((current) => ({ ...current, [observationId]: categoryId }))}
        onError={model.setError}
        onAdded={async () => {
          await model.reload();
          onChanged();
        }}
      />
    </section>
  );
}
